import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Hook adaptado para usar formulario_plano_acao_id em vez de conformidade_id

interface DiagnosticoA3Formulario {
  id: string;
  formulario_plano_acao_id: string;
  situacao_atual: string;
  impacto_observado: string;
  causa_raiz_identificada: string;
  escopo_plano?: string | null;
  areas_envolvidas?: string[] | null;
  porque_1?: string | null;
  porque_2?: string | null;
  porque_3?: string | null;
  porque_4?: string | null;
  porque_5?: string | null;
  elaborado_por?: string | null;
  elaborado_em: string;
  revisado_por?: string | null;
  revisado_em?: string | null;
  elaborador?: { id: string; name: string } | null;
  revisor?: { id: string; name: string } | null;
}

interface DiagnosticoA3FormularioInsert {
  id?: string;
  formulario_plano_acao_id: string;
  situacao_atual: string;
  impacto_observado: string;
  causa_raiz_identificada: string;
  escopo_plano?: string | null;
  areas_envolvidas?: string[] | null;
  porque_1?: string | null;
  porque_2?: string | null;
  porque_3?: string | null;
  porque_4?: string | null;
  porque_5?: string | null;
  elaborado_por?: string | null;
}

export function useDiagnosticoA3Formulario(formularioPlanoAcaoId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['diagnostico-a3-formulario', formularioPlanoAcaoId],
    queryFn: async () => {
      if (!formularioPlanoAcaoId) return null;
      const { data, error } = await supabase
        .from('diagnostico_a3')
        .select(`
          *,
          elaborador:users!diagnostico_a3_elaborado_por_fkey(id, name),
          revisor:users!diagnostico_a3_revisado_por_fkey(id, name)
        `)
        .eq('formulario_plano_acao_id', formularioPlanoAcaoId)
        .maybeSingle();
      if (error) throw error;
      return data as DiagnosticoA3Formulario | null;
    },
    enabled: !!formularioPlanoAcaoId
  });

  const upsertMutation = useMutation({
    mutationFn: async (data: DiagnosticoA3FormularioInsert) => {
      // Criar payload compatível com a tabela
      const payload: Record<string, unknown> = {
        situacao_atual: data.situacao_atual,
        impacto_observado: data.impacto_observado,
        causa_raiz_identificada: data.causa_raiz_identificada,
        escopo_plano: data.escopo_plano,
        areas_envolvidas: data.areas_envolvidas,
        porque_1: data.porque_1,
        porque_2: data.porque_2,
        porque_3: data.porque_3,
        porque_4: data.porque_4,
        porque_5: data.porque_5,
        elaborado_por: data.elaborado_por,
        formulario_plano_acao_id: data.formulario_plano_acao_id
      };

      if (data.id) {
        payload.id = data.id;
      }

      const { data: result, error } = await supabase
        .from('diagnostico_a3')
        .upsert(payload as any, { onConflict: 'id' })
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostico-a3-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
      toast({ title: 'Diagnóstico A3 salvo com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao salvar diagnóstico', description: error.message, variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('diagnostico_a3').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diagnostico-a3-formulario', formularioPlanoAcaoId] });
      toast({ title: 'Diagnóstico excluído' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir diagnóstico', description: error.message, variant: 'destructive' });
    }
  });

  // Validação de campos obrigatórios
  const isDiagnosticoCompleto = (diagnostico: DiagnosticoA3Formulario | null): boolean => {
    if (!diagnostico) return false;
    return !!(
      diagnostico.situacao_atual &&
      diagnostico.impacto_observado &&
      diagnostico.causa_raiz_identificada
    );
  };

  return {
    diagnostico: query.data,
    isLoading: query.isLoading,
    error: query.error,
    saveDiagnostico: upsertMutation.mutate,
    deleteDiagnostico: deleteMutation.mutate,
    isSaving: upsertMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDiagnosticoCompleto: isDiagnosticoCompleto(query.data)
  };
}
